/**
 * Sanitise the post-login redirect target read from `?nextUrl=` (ER-12027).
 *
 * Only same-origin, relative paths are allowed. The string must:
 *   - be a non-empty string starting with a single `/`,
 *   - not start with `//` (protocol-relative URL, e.g. `//evil.example.com`),
 *   - not start with `/\` (a backslash normalises to a protocol-relative URL).
 *
 * Returns the sanitised path/query on success and `null` when the value is
 * unsafe or unusable — callers then fall back to the default logged-in route.
 */
export function sanitizeNextUrl(nextUrl) {
  if (typeof nextUrl !== "string") {
    return null;
  }

  const trimmed = nextUrl.trim();
  if (!trimmed) {
    return null;
  }

  // Must be a same-origin relative path (starts with a single slash).
  if (!trimmed.startsWith("/")) {
    return null;
  }

  // Protocol-relative URL: `//host/…` — reject outright.
  if (trimmed.startsWith("//")) {
    return null;
  }

  // `/\host/…` resolves as protocol-relative in browsers — reject.
  if (/^\/\\/i.test(trimmed)) {
    return null;
  }

  return trimmed;
}

/** Read a single query param from a relative path/URL without a base origin. */
export function getNextUrlQueryParam(pathOrUrl, key) {
  if (typeof pathOrUrl !== "string" || !key) {
    return null;
  }

  const queryIndex = pathOrUrl.indexOf("?");
  if (queryIndex === -1) {
    return null;
  }

  return new URLSearchParams(pathOrUrl.slice(queryIndex + 1)).get(key);
}

/** Remove a query param from a relative path/URL, preserving all other params. */
export function stripNextUrlQueryParam(pathOrUrl, key) {
  if (typeof pathOrUrl !== "string") {
    return null;
  }

  const queryIndex = pathOrUrl.indexOf("?");
  if (queryIndex === -1) {
    return pathOrUrl;
  }

  const path = pathOrUrl.slice(0, queryIndex);
  const params = new URLSearchParams(pathOrUrl.slice(queryIndex + 1));
  params.delete(key);
  const serialized = params.toString();

  return serialized ? `${path}?${serialized}` : path;
}
